// Visit flow model for the dashboard card: Checked in -> Roomed -> With
// provider -> Orders -> Checked out, plus the one status chip ("where · how
// long") and the checkout reminder. Pure functions (no React), so the card, the
// queue toolbar count and the node test script all run the same code. The only
// state is two in-memory maps: "first seen" (see firstSeenAt below) and the
// "Still here" snoozes (see snoozeStillHere).
// Test: node --no-warnings src/landingscreen/cards/visitflow.test.mjs
//   (re-runs itself under TZ=UTC, America/Chicago, Asia/Kolkata and
//   Pacific/Auckland)
//
// Inputs are one row of POST /v1/{p}/appointments/dashboard:
// - signintime: journey stamps, "YYYY-MM-DD HH:MM:SS" naive clinic time
//   (America/Chicago), whatever time zone the browser is in:
//   printedAt, ReadyForTriageTime, RoomAssignTime, ReadyForProviderTime,
//   ReadyForCheckoutTime, BlinkscribeRecordingStartTime/EndTime (the LATEST
//   recording; each new one overwrites), and the newer keys CheckedOutTime,
//   FirstRecordingStartTime, FutureApptMaxIdAtCheckin (absent on older visits),
//   AutoCheckedOut (set with CheckedOutTime by the auto-checkout job).
//   Only stamps dated the card's own day count (a cloned or reused row can
//   carry another day's stamps).
// - visit_confirmation, room_no, token, purpose, allappts, orders (ts is naive
//   clinic time too), lov, startdate/starttime, next_appointment,
//   future_appt_max_id.
// - orders may include the dashboard's display-only VISIT row (synthetic: true,
//   id null) for a visit with a level of visit but no VISIT order: it counts as
//   a provider visit, never as an open order or a COMPLETED VISIT (visitrow.js).

import { isSyntheticOrder } from "./visitrow.js";

export const WAIT_AMBER_MINS = 15;
export const WAIT_RED_MINS = 30;
// Checkout reminder: minutes after the provider (or, for a procedures-only
// visit, the last completed procedure) when "Check out?" fires without a
// follow-up booked since check-in. Procedures-only patients mostly booked their
// follow-up before the visit, so the booking signal rarely fires for them.
export const CHECKOUT_NUDGE_MINS = 30;
export const PROCEDURE_CHECKOUT_NUDGE_MINS = 10;
// "Still here?" (owner option b, 09-29; chart 8823): an order pending this long
// after the provider, with a follow-up booked, means the patient most likely
// left without it being marked done. "Still here" hides the prompt for 30 min.
export const STILL_HERE_MINS = 60;
export const STILL_HERE_SNOOZE_MINS = 30;
// A recording left "running" (start > end) this long after it started, on a
// visit whose VISIT order is COMPLETED, is an abandoned LiveScribe session and
// no longer holds the patient "With provider".
export const STALE_RECORDING_MINS = 90;
// A telehealth encounter form printed more than this long before the
// scheduled start is a pre-print, not a check-in (unless a recording exists).
export const TELE_PRINT_WINDOW_MINS = 60;
// Placeholder appointment id (INT max) seen on junk rows.
export const APPT_ID_PLACEHOLDER = 2147483647;
// Journey stamps are naive wall-clock times of the clinic.
export const CLINIC_TIME_ZONE = "America/Chicago";

const CLOSED_ORDER_STATUSES = ["COMPLETED", "CANCELLEDTASK"];
// In-clinic work: the only order types that can hold up checkout or count as
// a procedures-only visit. Matches lab/labs, injection/injections,
// procedure/"Procedures/Tests", diagnostic test(s), vitamin. Everything else
// (Notes, Alerts, VISIT, Refills, Prescription(s), Referrals, Task,
// Schedule-RTO, schedule, mrecord, and any new type) never blocks.
const CLINICAL_ORDER_RE = /^(lab|injection|procedure|diagnostic test|vitamin)/;
const NOT_A_VISIT_RE = /cancel|no[\s-]?show|rescheduled/;
const WAITING_ROOM_RE = /^waiting(\s*room)?$/i;
const TELEHEALTH_RE = /telehealth/i;
// Statuses past "Ready for Provider". "Processing" is also used by
// procedures-only visits, so it counts as provider done only with a level of
// visit set (lov > 0).
const NAIVE_DATE_RE = /^(\d{4}-\d{2}-\d{2})(?:[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?$/;
const NAIVE_STAMP_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?$/;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const HOUR_MS = 3600000;

// ---- clinic time (America/Chicago), independent of the browser's zone -------

let clinicFormatter = null;
const getClinicFormatter = () => {
  if (!clinicFormatter) {
    clinicFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: CLINIC_TIME_ZONE,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }
  return clinicFormatter;
};

// Clinic UTC offset (ms, negative for Chicago) at an instant. Chicago changes
// offset only on whole UTC hours, so the result is cached per hour.
const offsetCache = new Map();
const clinicOffsetAt = (ms) => {
  const bucket = Math.floor(ms / HOUR_MS);
  const cached = offsetCache.get(bucket);
  if (cached !== undefined) return cached;
  const probe = bucket * HOUR_MS;
  const parts = {};
  getClinicFormatter()
    .formatToParts(new Date(probe))
    .forEach((p) => {
      parts[p.type] = p.value;
    });
  const wallAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  );
  const offset = wallAsUtc - probe;
  if (offsetCache.size > 5000) offsetCache.clear();
  offsetCache.set(bucket, offset);
  return offset;
};

// The instant of a clinic wall-clock time. Across DST: a time in the spring
// gap moves forward an hour; an ambiguous fall-back time takes one of the two.
const clinicWallToDate = (y, mo, d, h = 0, mi = 0, s = 0, ms = 0) => {
  const wallAsUtc = Date.UTC(y, mo - 1, d, h, mi, s, ms);
  const first = clinicOffsetAt(wallAsUtc);
  let t = wallAsUtc - first;
  const second = clinicOffsetAt(t);
  if (second !== first) t = wallAsUtc - second;
  return new Date(t);
};

// Clinic wall clock of an instant, read with getUTC* getters.
const clinicWall = (d) => new Date(d.getTime() + clinicOffsetAt(d.getTime()));

const pad2 = (n) => String(n).padStart(2, "0");

// "YYYY-MM-DD" of an instant in clinic time.
export const clinicDateKey = (d) => {
  const w = clinicWall(d);
  return `${w.getUTCFullYear()}-${pad2(w.getUTCMonth() + 1)}-${pad2(w.getUTCDate())}`;
};

export const clinicTodayKey = (now = new Date()) => clinicDateKey(now);

// "YYYY-MM-DD HH:MM:SS" clinic time, the format of the journey stamps.
export const clinicStamp = (now = new Date()) => {
  const w = clinicWall(now);
  return `${clinicDateKey(now)} ${pad2(w.getUTCHours())}:${pad2(w.getUTCMinutes())}:${pad2(
    w.getUTCSeconds(),
  )}`;
};

export const parseJourney = (signintime) => {
  if (!signintime) return {};
  if (typeof signintime === "object") return signintime;
  try {
    const parsed = JSON.parse(signintime);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

// Naive "YYYY-MM-DD[ T]HH:MM[:SS[.fff]]" (journey stamps, order ts) is clinic
// time; a string with an explicit zone ("Z", "+05:30") is taken as written.
export const parseStamp = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const s = String(v).trim();
  if (!s) return null;
  const m = s.match(NAIVE_STAMP_RE);
  if (m) {
    const [, y, mo, d, h, mi, sec, frac] = m;
    const out = clinicWallToDate(
      Number(y),
      Number(mo),
      Number(d),
      Number(h || 0),
      Number(mi || 0),
      Number(sec || 0),
      frac ? Number(frac.slice(0, 3).padEnd(3, "0")) : 0,
    );
    return Number.isNaN(out.getTime()) ? null : out;
  }
  const d = new Date(s.replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};

const earliest = (...dates) =>
  dates.reduce((acc, d) => (d && (!acc || d < acc) ? d : acc), null);

const latest = (...dates) =>
  dates.reduce((acc, d) => (d && (!acc || d > acc) ? d : acc), null);

// Whole clock minutes between two instants (9:12:36 -> 9:32:00 is 20), i.e.
// what a person reads off the HH:MM stamps. Never negative.
export const minutesBetween = (from, to) =>
  from && to
    ? Math.max(0, Math.floor(to.getTime() / 60000) - Math.floor(from.getTime() / 60000))
    : null;

export const fmtElapsed = (mins) => {
  if (mins === null || mins === undefined) return "";
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

const withTime = (label, mins) =>
  mins === null || mins === undefined ? label : `${label} · ${fmtElapsed(mins)}`;

// "9:23 AM", clinic time: the chip tooltips say what each clock counts from.
const fmtClock = (d) => {
  if (!d) return "";
  const w = clinicWall(d);
  const h = w.getUTCHours();
  return `${h % 12 || 12}:${pad2(w.getUTCMinutes())} ${h >= 12 ? "PM" : "AM"}`;
};

// Order name for the chip: tags stripped, cut to fit ("PREVNAR 20 pending").
const ORDER_NAME_MAX = 28;
const orderFullName = (o) =>
  String(o?.ordername || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
const orderShortName = (o) => {
  const name = orderFullName(o);
  return name.length > ORDER_NAME_MAX
    ? `${name.slice(0, ORDER_NAME_MAX - 1).trimEnd()}…`
    : name;
};
// Full name for lists (tooltips, the confirm): the type when the name is blank.
const orderLabel = (o) => orderFullName(o) || String(o?.ordertype || "").trim() || "Order";

// "A", "A and B", "A, B and C".
const joinNames = (names) =>
  names.length <= 1
    ? names.join("")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

// The "Mark done & check out" confirm: "Mark PREVNAR 20 as done and check the
// patient out?" (every open order is named: marking an injection done attests
// it was given).
export const stillHereConfirmText = (orders) => {
  const names = (Array.isArray(orders) ? orders : []).map(orderLabel);
  if (!names.length) return "Check the patient out?";
  return `Mark ${joinNames(names)} as done and check the patient out?`;
};

// A journey flag (AutoCheckedOut) written as true / 1 / "1" / "true" / a time.
const isFlagSet = (v) => {
  if (v === null || v === undefined || v === false) return false;
  if (typeof v === "number") return v !== 0;
  const s = String(v).trim().toLowerCase();
  return s !== "" && s !== "0" && s !== "false" && s !== "no" && s !== "null";
};

// The appointment's clinic day, "YYYY-MM-DD", from its startdate.
export const startDateKey = (startdate) => {
  const s = String(startdate || "").trim();
  if (!s) return null;
  const m = s.match(NAIVE_DATE_RE);
  if (m) return m[1];
  const d = parseStamp(s);
  return d ? clinicDateKey(d) : null;
};

// A card dated before the clinic's today.
export const isPastClinicDay = (startdate, now = new Date()) => {
  const day = startDateKey(startdate);
  return Boolean(day) && day < clinicDateKey(now);
};

// Whether a journey stamp is dated the appointment's own clinic day.
export const isStampOnDay = (v, startdate) => {
  const d = parseStamp(v);
  if (!d) return false;
  const day = startDateKey(startdate);
  return !day || clinicDateKey(d) === day;
};

// Scheduled start instant from startdate (+ starttime when present).
const scheduledStartAt = (data) => {
  const day = startDateKey(data?.startdate);
  if (!day) return null;
  const t =
    String(data?.starttime || "").trim().match(/^(\d{1,2}):(\d{2})/) ||
    String(data?.startdate || "").trim().match(/[ T](\d{2}):(\d{2})/);
  if (!t) return null;
  return parseStamp(`${day} ${pad2(Number(t[1]))}:${t[2]}:00`);
};

const orderType = (o) => String(o?.ordertype || "").trim().toUpperCase();
const orderStatus = (o) => String(o?.status || "").trim().toUpperCase();

// The morning job's per-patient "The following are covered for this patient:
// ..." note. It repeats the coverage chips, so the card lists it behind the
// "i" button instead of as a task.
export const isCoverageNote = (o) =>
  orderType(o) === "NOTES" &&
  String(o?.ordername || "")
    .trim()
    .toLowerCase()
    .startsWith("the following are covered");

// ---- coverage note save time (owner 10-19) ----------------------------------
// A coverage note's ts is its visit-day key. Older notes carry that day at
// 00:00:00 with no real time (the gateway wrote ts = the visit date), which
// read as "12:00 AM". The gateway now also records the real save instant in
// tasklog: {"coverage_saved_at": "YYYY-MM-DD HH:MM:SS" (clinic), "by": login}.
// A ts with a real clock time (a note saved on the visit day) is a real save
// time too. Without either, only the date is known: never a made-up 12 AM.
const parseTasklog = (v) => {
  if (!v) return {};
  if (typeof v === "object") return Array.isArray(v) ? {} : v;
  try {
    const parsed = JSON.parse(v);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const isClinicMidnight = (d) => {
  const w = clinicWall(d);
  return (
    w.getUTCHours() === 0 &&
    w.getUTCMinutes() === 0 &&
    w.getUTCSeconds() === 0 &&
    w.getUTCMilliseconds() === 0
  );
};

// "Oct 19", clinic time.
const fmtClinicDay = (d) => {
  const w = clinicWall(d);
  return `${MONTHS[w.getUTCMonth()]} ${w.getUTCDate()}`;
};

// { at: real save instant | null, day: the note's date when no real time is
// known | null, by: who saved it ("" when unknown) }.
export const coverageNoteSaved = (note) => {
  const log = parseTasklog(note?.tasklog);
  const by = String(log.by || note?.provider || "").trim();
  const logged = parseStamp(log.coverage_saved_at);
  if (logged && !isClinicMidnight(logged)) return { at: logged, day: null, by };
  const ts = parseStamp(note?.ts);
  if (!ts) return { at: null, day: null, by };
  if (isClinicMidnight(ts)) return { at: null, day: ts, by };
  return { at: ts, day: null, by };
};

// "Saved Oct 19, 3:12 PM by asmith" with a real save time, "Oct 19" for an
// older note (date only), "" when the note has no usable date.
export const coverageNoteLabel = (note) => {
  const { at, day, by } = coverageNoteSaved(note);
  if (at) return `Saved ${fmtClinicDay(at)}, ${fmtClock(at)}${by ? ` by ${by}` : ""}`;
  if (day) return fmtClinicDay(day);
  return "";
};

// The time line Tasks shows under "Ordered by" (owner 10-19, same rule as the
// coverage modal). A real save time from tasklog.coverage_saved_at wins (the
// coverage note and the copay "Payment" order); else ts. A ts at exactly
// clinic midnight is a bare date (the gateway wrote ts = the visit date), so
// only the date shows: never a made-up 12:00 AM. "" when nothing parses.
// hourPad: "hh" style ("09:05 AM") instead of "h" ("9:05 AM").
export const orderTimeLabel = (order, { hourPad = false } = {}) => {
  const log = parseTasklog(order?.tasklog);
  const logged = parseStamp(log.coverage_saved_at);
  const ts = parseStamp(order?.ts);
  const at = logged && !isClinicMidnight(logged) ? logged : ts && !isClinicMidnight(ts) ? ts : null;
  const d = at || ts;
  if (!d) return "";
  const w = clinicWall(d);
  const date = `${pad2(w.getUTCMonth() + 1)}/${pad2(w.getUTCDate())}/${w.getUTCFullYear()}`;
  if (!at) return date;
  const h = w.getUTCHours() % 12 || 12;
  return `${date} ${hourPad ? pad2(h) : h}:${pad2(w.getUTCMinutes())} ${w.getUTCHours() >= 12 ? "PM" : "AM"}`;
};

// Newest first by real save time. Notes without one were written before the
// save time was recorded, so they come after every note that has one, newest
// (highest id) first.
export const compareCoverageNotes = (a, b) => {
  const ta = coverageNoteSaved(a).at?.getTime() ?? null;
  const tb = coverageNoteSaved(b).at?.getTime() ?? null;
  if (ta !== null && tb !== null && ta !== tb) return tb - ta;
  if (ta !== null && tb === null) return -1;
  if (ta === null && tb !== null) return 1;
  return Number(b?.id || 0) - Number(a?.id || 0);
};

export const isClinicalOrder = (o) =>
  CLINICAL_ORDER_RE.test(
    String(o?.ordertype || "").trim().toLowerCase().replace(/\s+/g, " "),
  );

// schedule 't' = scheduled for a later date, not today's work.
const isScheduledLater = (o) =>
  String(o?.schedule || "").trim().toLowerCase() === "t";

// Blocking-open = in-clinic work for today that is not done yet. Messages,
// alerts, refills, referrals, the VISIT (level-of-visit) row and anything
// scheduled for later never hold up checkout; nor does a display-only
// (synthetic) row, which nobody can close.
export const isOpenOrder = (o) =>
  !isSyntheticOrder(o) &&
  isClinicalOrder(o) &&
  !isScheduledLater(o) &&
  !CLOSED_ORDER_STATUSES.includes(orderStatus(o));

export const roomDisplayName = (roomNo) => {
  const r = String(roomNo || "").trim();
  if (!r) return "";
  return /^\d+$/.test(r) ? `Room ${r}` : r;
};

// Telehealth: the status says "Tele - ..." or the visit type (the card's, or
// any of the patient's same-day appointments') names TELEHEALTH.
export const isTelehealthVisit = (data) => {
  if (!data) return false;
  const vc = String(data.visit_confirmation || "").trim().toLowerCase();
  if (vc.startsWith("tele")) return true;
  if (TELEHEALTH_RE.test(String(data.purpose || ""))) return true;
  return (
    Array.isArray(data.allappts) &&
    data.allappts.some((a) => TELEHEALTH_RE.test(String(a?.purpose || "")))
  );
};

// Appointment ids for the follow-up comparison: a positive integer below the
// INT-max placeholder, else unknown (null).
const apptId = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 && n < APPT_ID_PLACEHOLDER ? n : null;
};

// ---- "first seen in this state" ---------------------------------------------
// Two done times have no reliable stamp: a procedures-only visit (orders_master
// ts is when the order was CREATED, not completed) and a provider done without
// a recording end or ReadyForCheckoutTime (a COMPLETED VISIT order, or lov with
// a later status). For those the nudge counts from when this browser tab first
// saw the card in that state. Limitation: the map lives in memory only, so it
// survives re-renders and background refreshes but not a page reload; after a
// reload the 10/30 min count starts again, and the chip shows no minutes.
const firstSeen = new Map();
const FIRST_SEEN_MAX = 5000;
const firstSeenKey = (data, state) => {
  const id = apptId(data?.appointmentid);
  if (id !== null) return `${id}|${state}`;
  const row = data?.id;
  return row === null || row === undefined || row === "" ? null : `row:${row}|${state}`;
};
// Records `now` the first time `active` is seen for this appointment + state,
// and forgets it once the state is left (so re-entering starts over).
export const firstSeenAt = (data, state, now, active) => {
  const key = firstSeenKey(data, state);
  if (!key) return null;
  if (!active) {
    firstSeen.delete(key);
    return null;
  }
  let t = firstSeen.get(key);
  if (t === undefined) {
    if (firstSeen.size >= FIRST_SEEN_MAX) firstSeen.clear();
    t = now.getTime();
    firstSeen.set(key, t);
  }
  return new Date(t);
};
// Tests only.
export const resetFirstSeen = () => firstSeen.clear();

// ---- "Still here" snooze ------------------------------------------------------
// The "still here?" prompt's "Still here" link hides it for that appointment
// for 30 min (the chip goes back to "<order> pending · Xm"). In memory only,
// like firstSeen: a page reload brings the prompt back. Cards re-read it via
// subscribeStillHere; an expired snooze comes back on the 30 s dashboard tick.
const stillHereSnoozes = new Map();
const stillHereListeners = new Set();
let stillHereVersion = 0;
export const subscribeStillHere = (listener) => {
  stillHereListeners.add(listener);
  return () => {
    stillHereListeners.delete(listener);
  };
};
export const getStillHereVersion = () => stillHereVersion;
export const snoozeStillHere = (data, now = new Date(), mins = STILL_HERE_SNOOZE_MINS) => {
  const key = firstSeenKey(data, "stillHere");
  if (!key) return false;
  if (stillHereSnoozes.size >= FIRST_SEEN_MAX) stillHereSnoozes.clear();
  stillHereSnoozes.set(key, now.getTime() + mins * 60000);
  stillHereVersion += 1;
  stillHereListeners.forEach((listener) => listener());
  return true;
};
export const isStillHereSnoozed = (data, now = new Date()) => {
  const key = firstSeenKey(data, "stillHere");
  const until = key ? stillHereSnoozes.get(key) : undefined;
  if (until === undefined) return false;
  if (now.getTime() < until) return true;
  stillHereSnoozes.delete(key);
  return false;
};
// Tests only.
export const resetStillHereSnoozes = () => {
  stillHereSnoozes.clear();
  stillHereVersion += 1;
};

// FutureApptMaxIdAtCheckin -> max id of the patient's future appointments at
// check-in; null/0 means there were none. Absent (older visits) or junk ->
// unknown, and the reminder falls back to the timer.
export const followUpBookedSinceCheckin = (journey, futureApptMaxId) => {
  if (!journey || !Object.prototype.hasOwnProperty.call(journey, "FutureApptMaxIdAtCheckin"))
    return false;
  const raw = journey.FutureApptMaxIdAtCheckin;
  const noneAtCheckin = raw === null || raw === "" || Number(raw) === 0;
  const baseline = noneAtCheckin ? 0 : apptId(raw);
  const current = apptId(futureApptMaxId);
  if (baseline === null || current === null) return false;
  return current > baseline;
};

// The card's booked follow-up: next_appointment, unless it is this visit
// itself, one of the patient's appointments that day (allappts), or dated on
// or before the card's day. Until the dashboard excludes those, a patient seen
// before their slot showed their own appointment as "Next".
export const followUpAppointment = (data) => {
  const next = data?.next_appointment;
  if (!next || typeof next !== "object" || !next.startdate) return null;
  const nextId = String(next.appointmentid ?? "").trim();
  if (nextId) {
    if (nextId === String(data.appointmentid ?? "").trim()) return null;
    if (
      Array.isArray(data.allappts) &&
      data.allappts.some((a) => String(a?.appointmentid ?? "").trim() === nextId)
    )
      return null;
  }
  const day = startDateKey(data.startdate);
  const nextDay = startDateKey(next.startdate);
  if (day && nextDay && nextDay <= day) return null;
  return next;
};

// "Oct 14, 9:00 AM" from the dashboard's next_appointment fields.
const fmtApptWhen = (startdate, starttime) => {
  const s = String(startdate || "").trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
  if (!m) return "";
  const day = `${MONTHS[Number(m[2]) - 1] || ""} ${Number(m[3])}`.trim();
  const t = String(starttime || "").trim().match(/^(\d{1,2}):(\d{2})/);
  const hh = t ? Number(t[1]) : m[4] && `${m[4]}:${m[5]}` !== "00:00" ? Number(m[4]) : null;
  const mm = t ? t[2] : m[5];
  if (hh === null || hh > 23) return day;
  return `${day}, ${hh % 12 || 12}:${mm} ${hh >= 12 ? "PM" : "AM"}`;
};

export const nextApptText = (next) => {
  if (!next || typeof next !== "object" || !next.startdate) return null;
  const when = fmtApptWhen(next.startdate, next.starttime);
  if (!when) return null;
  const provider = String(next.provider || "").trim();
  return provider ? `Next: ${when} · ${provider}` : `Next: ${when}`;
};

const waitTone = (mins) =>
  mins === null
    ? "neutral"
    : mins >= WAIT_RED_MINS
      ? "red"
      : mins >= WAIT_AMBER_MINS
        ? "amber"
        : "neutral";

const teleWaitText = (mins) =>
  mins === null || mins === undefined
    ? "Telehealth · waiting"
    : `Telehealth · waiting ${fmtElapsed(mins)}`;

// Returns null for cards with no visit to track (not checked in yet,
// cancelled, no-show, rescheduled).
export const visitModel = (data, now = new Date()) => {
  if (!data) return null;
  const j = parseJourney(data.signintime);
  const vc = String(data.visit_confirmation || "").trim().toLowerCase();
  if (NOT_A_VISIT_RE.test(vc)) return null;

  const orders = Array.isArray(data.orders) ? data.orders : [];
  const checkedOut = vc === "checked out";
  const readyForCheckout = vc === "ready for checkout";
  const roomNo = String(data.room_no || "").trim();
  const inWaitingRoom = WAITING_ROOM_RE.test(roomNo);
  const token = Number(data.token) || 0;
  const tele = isTelehealthVisit(data);

  const apptDay = startDateKey(data.startdate);
  const isPastDay = Boolean(apptDay) && apptDay < clinicDateKey(now);
  // A stamp from another day (a cloned or reused row) is not this visit's.
  const stampOnDay = (v) => {
    const d = parseStamp(v);
    return d && (!apptDay || clinicDateKey(d) === apptDay) ? d : null;
  };

  const latestStart = stampOnDay(j.BlinkscribeRecordingStartTime);
  const latestEnd = stampOnDay(j.BlinkscribeRecordingEndTime);
  const firstStart = stampOnDay(j.FirstRecordingStartTime);
  const hasRecordingStamps = Boolean(latestStart || latestEnd || firstStart);

  // Check-in: the encounter form is printed ~2 min before the status flips, so
  // take whichever came first. A telehealth form printed on its own (no token,
  // no ReadyForTriageTime) more than an hour before the slot is a pre-print,
  // not a check-in, unless a recording shows the visit happened.
  const printedAt = stampOnDay(j.printedAt);
  const triageAt = stampOnDay(j.ReadyForTriageTime);
  let printCounts = Boolean(printedAt);
  if (printedAt && tele && !triageAt && token <= 0 && !hasRecordingStamps) {
    const slot = scheduledStartAt(data);
    printCounts =
      !slot || printedAt.getTime() >= slot.getTime() - TELE_PRINT_WINDOW_MINS * 60000;
  }
  const checkinAt = earliest(printCounts ? printedAt : null, triageAt);
  const checkinDone = Boolean(checkinAt) || token > 0 || Boolean(roomNo) || checkedOut;
  if (!checkinDone) return null; // pre-visit: no timeline noise on scheduled cards

  // Rooming: the MA saves vitals, room and Ready for Provider in one action, so
  // the two stamps usually match. Putting the patient in "Waiting Room" also
  // stamps RoomAssignTime, and that is not rooming.
  const roomedAt = earliest(
    inWaitingRoom ? null : stampOnDay(j.RoomAssignTime),
    stampOnDay(j.ReadyForProviderTime),
  );
  const roomedEvidence = Boolean(roomedAt) || (Boolean(roomNo) && !inWaitingRoom);

  // Provider: BlinkScribe overwrites the latest start/end on every recording,
  // so the first start (when present) anchors the clock and a reconnect or a
  // second recording cannot reset it. start > end = a recording is running,
  // unless it has been "running" 90+ min on a visit whose VISIT order is
  // COMPLETED (an abandoned LiveScribe session).
  const providerStartAt = earliest(firstStart, latestStart);
  // A display-only VISIT row (synthetic, from the level of visit) is a
  // provider visit, but only a real VISIT order can be COMPLETED.
  const hasVisitOrder = orders.some((o) => orderType(o) === "VISIT");
  const visitCompleted = orders.some(
    (o) =>
      orderType(o) === "VISIT" &&
      orderStatus(o) === "COMPLETED" &&
      !isSyntheticOrder(o),
  );
  // journey_lov = max level of visit across the patient's same-day rows
  // (dashboard merge); fall back to the card row's own lov.
  const lovSet = Number(data.journey_lov ?? data.lov) > 0;
  const runningRecording = Boolean(latestStart) && (!latestEnd || latestStart > latestEnd);
  const staleRecording =
    runningRecording &&
    visitCompleted &&
    minutesBetween(latestStart, now) >= STALE_RECORDING_MINS;
  const recording = runningRecording && !staleRecording;
  // Done without (or beyond) the recording stamps: a COMPLETED VISIT order.
  // Not "level of visit set + Processing": lov can be filled before the
  // provider sees the patient, which ended the wait early (review 09-29).
  const providerSignal = visitCompleted;
  const providerDone =
    readyForCheckout ||
    checkedOut ||
    (!recording && (Boolean(latestEnd) || providerSignal));
  const readyForCheckoutAt = stampOnDay(j.ReadyForCheckoutTime);
  // Latest recording end, else ReadyForCheckoutTime, else unknown (never the
  // VISIT order ts: orders_master.ts is the creation time).
  const providerDoneAt = providerDone
    ? (!recording && latestEnd) || readyForCheckoutAt || null
    : null;

  const openOrders = orders.filter(isOpenOrder);
  const openOrderCount = openOrders.length;
  // When the oldest open blocking order was created (today's only): how long
  // the orders have been pending.
  const oldestOpenOrderAt = earliest(...openOrders.map((o) => stampOnDay(o.ts)));

  // Imaging / injection / lab-only visits never see a provider: nothing was
  // recorded, there is no level-of-visit task, and the procedures are done.
  // Completed in-clinic work for today (same allowlist as blocking orders).
  const completedProcedures = orders.filter(
    (o) =>
      isClinicalOrder(o) &&
      !isScheduledLater(o) &&
      orderStatus(o) === "COMPLETED",
  );
  const noProviderVisit =
    !hasRecordingStamps &&
    !hasVisitOrder &&
    !lovSet &&
    completedProcedures.length > 0 &&
    openOrderCount === 0;
  // Creation time of the latest completed procedure (orders_master.ts is never
  // updated on completion), so only a lower bound for "done".
  const proceduresDoneAt = noProviderVisit
    ? latest(...completedProcedures.map((o) => parseStamp(o.ts)))
    : null;

  // Follow-up booked since check-in: Athena ids are monotonic, so a future
  // appointment with a larger id than any at check-in was booked afterwards.
  const followUpBooked = followUpBookedSinceCheckin(j, data.future_appt_max_id);

  // When the patient became ready to leave. Reliable when it comes from a
  // stamp (recording end, ReadyForCheckoutTime); otherwise the later of the
  // best stamp and when this tab first saw the state (see firstSeenAt), and
  // then the chip shows no minutes.
  const tracking = !isPastDay && !checkedOut;
  const proceduresSeenAt = firstSeenAt(
    data,
    "procedures",
    now,
    tracking && noProviderVisit && !providerDoneAt,
  );
  const providerSeenAt = firstSeenAt(
    data,
    "provider",
    now,
    tracking && !noProviderVisit && providerDone && !providerDoneAt,
  );
  let doneAt = null;
  let doneAtReliable = false;
  if (providerDoneAt) {
    doneAt = providerDoneAt;
    doneAtReliable = true;
  } else if (noProviderVisit) {
    doneAt = latest(proceduresSeenAt, proceduresDoneAt);
  } else if (providerDone) {
    doneAt = providerSeenAt;
  }

  // Checkout reminder: the patient is done with the provider (or the
  // procedures), nothing is open, and either a follow-up was booked since
  // check-in or the wait passed the nudge time (30 min after the provider,
  // 10 after the last procedure). Today only; checkout stays manual.
  const minsSinceDone = minutesBetween(doneAt, now);
  const shownDoneMins = doneAtReliable ? minsSinceDone : null;
  const nudgeMins = noProviderVisit ? PROCEDURE_CHECKOUT_NUDGE_MINS : CHECKOUT_NUDGE_MINS;
  const checkoutDue =
    !checkedOut &&
    !isPastDay &&
    (noProviderVisit || providerDone) &&
    openOrderCount === 0 &&
    (followUpBooked || (minsSinceDone !== null && minsSinceDone >= nudgeMins));

  // Orders pending: the provider is done and in-clinic work is still open
  // (a no-provider visit has no open orders by definition). Its clock runs from
  // the later of the provider finishing and the oldest open order being
  // created; with no reliable provider-done stamp, from the order's creation
  // (how long it has been pending); with neither, no minutes.
  const ordersPending = !checkedOut && !isPastDay && providerDone && openOrderCount > 0;
  const ordersSince = providerDoneAt
    ? latest(providerDoneAt, oldestOpenOrderAt)
    : oldestOpenOrderAt;
  const ordersMins = ordersPending ? minutesBetween(ordersSince, now) : null;

  // "Still here?" (owner option b, 09-29; chart 8823: PREVNAR 20 never marked
  // done, provider done 09:28, follow-up booked 09:34, patient gone, the card
  // said "PREVNAR 20 pending · 2h 48m" and nothing could close it). Orders
  // pending an hour or more, nothing recording, and a follow-up booked: since
  // check-in (FutureApptMaxIdAtCheckin), or, on visits checked in before that
  // stamp existed, any real future appointment. The desk confirms "Mark done &
  // check out" or snoozes it ("Still here").
  const hasCheckinBaseline =
    Object.prototype.hasOwnProperty.call(j, "FutureApptMaxIdAtCheckin");
  const followUp = followUpAppointment(data);
  const followUpKnown = followUpBooked || (!hasCheckinBaseline && Boolean(followUp));
  // Its own clock: with no provider-done stamp (VISIT order COMPLETED only),
  // count from when this tab first saw the provider done, not from the order's
  // creation, or it fired the moment the VISIT order closed (review 09-29).
  const stillHereSince = providerDoneAt
    ? latest(providerDoneAt, oldestOpenOrderAt)
    : providerSeenAt
      ? latest(providerSeenAt, oldestOpenOrderAt)
      : null;
  const stillHereMins = ordersPending ? minutesBetween(stillHereSince, now) : null;
  // Only for a patient who has actually arrived today and whose slot has come:
  // overnight test recordings made visits booked for later today look finished
  // (1468 / 2336, 09-29).
  const slotAt = scheduledStartAt(data);
  const slotReached = !slotAt || slotAt.getTime() <= now.getTime();
  const stillHereDue =
    ordersPending &&
    !recording &&
    followUpKnown &&
    Boolean(checkinAt) &&
    slotReached &&
    stillHereMins !== null &&
    stillHereMins >= STILL_HERE_MINS;
  const stillHereSnoozed = stillHereDue && isStillHereSnoozed(data, now);
  const stillHere = stillHereDue && !stillHereSnoozed;

  const checkedOutAt = stampOnDay(j.CheckedOutTime);
  const roomedDone = roomedEvidence || Boolean(providerStartAt) || providerDone || checkedOut;
  const ordersDone =
    checkedOut || ((providerDone || noProviderVisit) && openOrderCount === 0);
  const step = (key, label, state, at = null) => ({
    key,
    label,
    state,
    done: state === "done",
    skipped: state === "skipped",
    at,
  });
  const steps = [
    step("checkin", "Checked in", "done", checkinAt),
    step(
      "roomed",
      "Roomed",
      noProviderVisit && !roomedEvidence ? "skipped" : roomedDone ? "done" : "todo",
      roomedAt,
    ),
    step(
      "provider",
      "With provider",
      noProviderVisit ? "skipped" : providerDone ? "done" : "todo",
      providerStartAt,
    ),
    step("orders", "Orders", ordersDone ? "done" : "todo"),
    step("checkout", "Checked out", checkedOut ? "done" : "todo", checkedOutAt),
  ];
  const currentIdx = steps.findIndex((s) => s.state === "todo");

  // The one chip: the state the patient is in now and how long they have been
  // in it (Waiting room / Waiting for provider / With provider / <order>
  // pending / Ready for checkout). Past days and checked-out visits never run
  // a clock. `title` (the chip's tooltip) says what the clock counts from.
  const since = (d) => minutesBetween(d, now);
  const autoCheckedOut = checkedOut && isFlagSet(j.AutoCheckedOut);
  let chip = null;
  if (checkedOut) {
    // An automatic checkout lands 30+ min after the visit really ended (the
    // job waits for quiet), so its visit length ends at the last activity the
    // job saw (AutoCheckoutLastActivity), never at the job's run time; without
    // that stamp there is no visit total (review 09-29).
    const autoLastActivityAt = autoCheckedOut ? stampOnDay(j.AutoCheckoutLastActivity) : null;
    const visitEndAt = autoCheckedOut ? autoLastActivityAt : checkedOutAt;
    const visitMins =
      checkinAt && visitEndAt && visitEndAt >= checkinAt ? minutesBetween(checkinAt, visitEndAt) : null;
    const autoTitle =
      (checkedOutAt
        ? `Checked out automatically at ${fmtClock(checkedOutAt)}`
        : "Checked out automatically") +
      (autoLastActivityAt ? `; last activity ${fmtClock(autoLastActivityAt)}` : "");
    if (visitMins !== null) {
      const waitedMins =
        providerStartAt && providerStartAt >= checkinAt
          ? minutesBetween(checkinAt, providerStartAt)
          : null;
      const totals =
        waitedMins === null
          ? `Visit ${fmtElapsed(visitMins)}`
          : `Visit ${fmtElapsed(visitMins)} · waited ${fmtElapsed(waitedMins)}`;
      chip = {
        kind: "totals",
        tone: "muted",
        mins: visitMins,
        waitedMins,
        auto: autoCheckedOut,
        text: autoCheckedOut ? `Checked out automatically · ${totals}` : totals,
        title: autoCheckedOut
          ? `${autoTitle}; checked in ${fmtClock(checkinAt)}`
          : `Checked in ${fmtClock(checkinAt)}, checked out ${fmtClock(checkedOutAt)}`,
      };
    } else if (autoCheckedOut) {
      // No visit total (no check-in stamp, no last-activity stamp, or checked
      // out on a later day); the wait still shows when it is known.
      const waitedMins =
        checkinAt && providerStartAt && providerStartAt >= checkinAt
          ? minutesBetween(checkinAt, providerStartAt)
          : null;
      chip = {
        kind: "checked-out",
        tone: "muted",
        mins: null,
        waitedMins,
        auto: true,
        text:
          waitedMins === null
            ? "Checked out automatically"
            : `Checked out automatically · waited ${fmtElapsed(waitedMins)}`,
        title: autoTitle,
      };
    } else if (j.CheckedOutTime && parseStamp(j.CheckedOutTime) && !checkedOutAt) {
      // Checked out on a later day (a catch-up checkout): no duration.
      chip = { kind: "checked-out", tone: "muted", mins: null, text: "Checked out" };
    }
  } else if (isPastDay) {
    chip = { kind: "not-checked-out", tone: "flag", mins: null, text: "Not checked out" };
  } else if (checkoutDue) {
    chip = {
      kind: "checkout",
      tone: "alert",
      mins: shownDoneMins,
      text: withTime("Check out?", shownDoneMins),
      title: doneAtReliable ? `Done since ${fmtClock(doneAt)}` : undefined,
    };
  } else if (noProviderVisit) {
    chip = {
      kind: "procedures",
      tone: "neutral",
      mins: shownDoneMins,
      text: withTime("Procedures done", shownDoneMins),
      title: doneAtReliable ? `Done since ${fmtClock(doneAt)}` : undefined,
    };
  } else if (ordersPending) {
    const mins = ordersMins;
    const single = openOrderCount === 1 ? orderShortName(openOrders[0]) : "";
    const openList = openOrders
      .slice(0, 5)
      .map((o) => {
        const name = orderLabel(o);
        const created = stampOnDay(o.ts);
        return created ? `${name} (ordered ${fmtClock(created)})` : name;
      })
      .join(", ");
    const label = single ? `${single} pending` : `Orders pending (${openOrderCount})`;
    const openTitle = `Open: ${openList}${openOrderCount > 5 ? ", …" : ""}${
      providerDoneAt ? `. Provider done ${fmtClock(providerDoneAt)}` : ""
    }`;
    if (stillHere) {
      const followUpWhen = followUp ? fmtApptWhen(followUp.startdate, followUp.starttime) : "";
      chip = {
        kind: "stillHere",
        tone: "amber",
        mins,
        text: `${label} · still here?`,
        title: `${openTitle}. Follow-up booked${
          followUpWhen ? ` for ${followUpWhen}` : ""
        }. Pending ${fmtElapsed(mins)}: if the patient has left, mark ${
          openOrderCount === 1 ? "it" : "them"
        } done and check out.`,
      };
    } else {
      chip = {
        kind: "orders",
        tone: "neutral",
        mins,
        text: withTime(label, mins),
        title: openTitle,
      };
    }
  } else if (providerDone) {
    const mins = shownDoneMins;
    chip = {
      kind: "ready",
      tone: "neutral",
      mins,
      text: withTime("Ready for checkout", mins),
      title: doneAtReliable ? `Provider done ${fmtClock(doneAt)}` : undefined,
    };
  } else if (providerStartAt) {
    const mins = since(providerStartAt);
    chip = {
      kind: "provider",
      tone: "neutral",
      mins,
      text: withTime("With provider", mins),
      title: `First recording started ${fmtClock(providerStartAt)}`,
    };
  } else if (roomedAt && !(tele && inWaitingRoom)) {
    // Roomed (vitals + Ready for Provider), no recording yet. A patient sent
    // back to the Waiting Room after intake is waiting for the provider too.
    const mins = since(roomedAt);
    chip = {
      kind: "room",
      tone: tele ? "neutral" : waitTone(mins),
      mins,
      text: withTime("Waiting for provider", mins),
      title: `${
        inWaitingRoom ? "Ready for provider" : roomDisplayName(roomNo) || "Roomed"
      } since ${fmtClock(roomedAt)}`,
    };
  } else if (roomedAt) {
    const mins = since(roomedAt);
    chip = { kind: "waiting", tone: "neutral", mins, text: teleWaitText(mins) };
  } else if (checkinAt && !roomedEvidence) {
    const mins = since(checkinAt);
    // A telehealth patient waits at home for the call: no amber/red.
    chip = tele
      ? { kind: "waiting", tone: "neutral", mins, text: teleWaitText(mins) }
      : {
          kind: "waiting",
          tone: waitTone(mins),
          mins,
          text: withTime("Waiting room", mins),
          title: `Checked in ${fmtClock(checkinAt)}`,
        };
  }
  // else: a manual check-in or rooming with no stamp - the steps show it, but
  // there is no clock to run.

  return {
    steps,
    currentIdx,
    chip,
    checkoutDue,
    checkedOut,
    autoCheckedOut,
    isPastDay,
    noProviderVisit,
    tele,
    recording,
    staleRecording,
    providerDone,
    openOrderCount,
    // The blocking-open orders themselves (the rows from data.orders), which
    // "Mark done & check out" completes.
    openOrders,
    followUpBooked,
    stillHere,
    stillHereSnoozed,
    // Shown beside "Check out?" so the desk sees whether a follow-up exists.
    nextApptText: nextApptText(followUp),
    times: {
      checkinAt,
      roomedAt,
      providerStartAt,
      providerDoneAt,
      proceduresDoneAt,
      doneAt,
      doneAtReliable,
      oldestOpenOrderAt,
      checkedOutAt,
    },
  };
};
